
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "posting-a-job-ad"
    }}>{`Posting a job ad`}</h1>
    <p>{`The SEEK API surfaces two mutations to post job ads, `}<a parentName="p" {...{
        "href": "#postposition"
      }}><inlineCode parentName="a">{`postPosition`}</inlineCode></a>{` and `}<a parentName="p" {...{
        "href": "#postpositionprofileforopening"
      }}><inlineCode parentName="a">{`postPositionProfileForOpening`}</inlineCode></a>{`.
Posting typically takes less than a minute after which a `}<a parentName="p" {...{
        "href": "#positionprofileposted"
      }}><inlineCode parentName="a">{`PositionProfilePosted`}</inlineCode>{` event`}</a>{` will be emitted.`}</p>
    <p>{`Job ads on SEEK have a maximum period of 30 days.
Your software must allow hirers to advertise a job on SEEK for this full period.`}</p>
    <h2 {...{
      "id": "idempotency"
    }}>{`Idempotency`}</h2>
    <p>{`Requests that carry a job posting `}<a parentName="p" {...{
        "href": "/graphql/in-practice#operations"
      }}>{`mutation`}</a>{` must be `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Idempotence#Computer_science_meaning"
      }}>{`idempotent`}</a>{`.
This removes the risk of unintentionally posting duplicate job ads when your software needs to `}<a parentName="p" {...{
        "href": "/graphql/error-responses#retrying-server-errors"
      }}>{`retry`}</a>{` a request due to an error.`}</p>
    <p>{`A unique `}<a parentName="p" {...{
        "href": "/schema/named-type/CreatePostingInstructionInput#/field/idempotencyId"
      }}><inlineCode parentName="a">{`idempotencyId`}</inlineCode></a>{` is required when posting a job ad.
We recommend that you generate a new `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Universally_unique_identifier#Version_4_(random)"
      }}>{`v4 UUID`}</a>{` before sending the first request to post each job ad, and to reuse that identifier on all subsequent retries of the same job ad.`}</p>
    <p>{`If your software has already posted a job ad with the same `}<inlineCode parentName="p">{`idempotencyId`}</inlineCode>{`,
the SEEK API will return a `}<a parentName="p" {...{
        "href": "/schema/named-type/PostPositionPayload_Conflict"
      }}><inlineCode parentName="a">{`PostPositionPayload_Conflict`}</inlineCode></a>{` or `}<a parentName="p" {...{
        "href": "/schema/named-type/PostPositionProfileForOpeningPayload_Conflict"
      }}><inlineCode parentName="a">{`PostPositionProfileForOpeningPayload_Conflict`}</inlineCode></a>{` result in accordance with our `}<a parentName="p" {...{
        "href": "/graphql/seek-api-conventions#mutation-conflicts"
      }}>{`mutation conflict`}</a>{` convention.
If your software was retrying a job posting request,
you can treat the conflict result as a success as it indicates the job ad has already been posted.
See the `}<a parentName="p" {...{
        "href": "#operations"
      }}>{`code samples below`}</a>{` for practical examples of these conflict results.`}</p>
    <p>{`The SEEK API deduplicates job ads posted by your software based on their `}<inlineCode parentName="p">{`idempotencyId`}</inlineCode>{`s.
Your software should never reuse an `}<inlineCode parentName="p">{`idempotencyId`}</inlineCode>{` when posting a different job ad;
this may result in subsequent job ads being missed or other unexpected behaviour.
Your identifiers are isolated and will not conflict with those generated by other recruitment software providers.`}</p>
    <h2 {...{
      "id": "operations"
    }}>{`Operations`}</h2>
    <p>{`The following sample mutations are provided for demonstration purposes only;
they are `}<a parentName="p" {...{
        "href": "/graphql/playground"
      }}>{`not executable in the Playground environment`}</a>{`.
You should use your live partner credentials to post a job ad under your `}<a parentName="p" {...{
        "href": "/introduction#integration-process"
      }}>{`unsearchable SEEK test hirer`}</a>{` for end-to-end testing.`}</p>
    <h3 {...{
      "id": "postposition"
    }}>{`postPosition`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/mutation/postPosition"
      }}><inlineCode parentName="a">{`postPosition`}</inlineCode>{` mutation`}</a>{` creates a new `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/position-openings"
      }}>{`position opening`}</a>{` and posts a `}<a parentName="p" {...{
        "href": "/schema/named-type/PositionProfile"
      }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode></a>{` in a single operation.`}</p>
    <pre><code parentName="pre" {...{
        "30": true,
        "8517": true,
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label=\\\"Mutation\\\"\",\"value\":\"mutation ($input: PostPositionInput!) {\\n  postPosition(input: $input) {\\n    ... on PostPositionPayload_Success {\\n      positionOpening {\\n        documentId {\\n          value\\n        }\\n      }\\n      positionProfile {\\n        profileId {\\n          value\\n        }\\n      }\\n    }\\n    ... on PostPositionPayload_Conflict {\\n      conflictingPositionOpening {\\n        documentId {\\n          value\\n        }\\n      }\\n      conflictingPositionProfile {\\n        profileId {\\n          value\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":46,\"column\":1,\"offset\":2773},\"end\":{\"line\":75,\"column\":4,\"offset\":3313},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Variables\\\"\",\"value\":\"{\\n  \\\"input\\\": {\\n    // Details of the position opening to create along with the job ad\\n    \\\"positionOpening\\\": {\\n      \\\"postingRequester\\\": {\\n        \\\"roleCode\\\": \\\"Company\\\",\\n\\n        // Hirer that will own the position opening\\n        \\\"id\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n        // Mary Manager is hiring this role as a hiring manager\\n        \\\"personContacts\\\": [\\n          {\\n            \\\"name\\\": { \\\"formattedName\\\": \\\"Mary Manager\\\" },\\n            \\\"roleCode\\\": \\\"HiringManager\\\",\\n            \\\"communication\\\": {\\n              \\\"email\\\": [{ \\\"address\\\": \\\"mary.manager@example.com\\\" }],\\n              \\\"phone\\\": [{ \\\"formattedNumber\\\": \\\"03 8517 4100\\\" }]\\n            }\\n          }\\n        ]\\n      }\\n    },\\n\\n    // Details of the job ad itself\\n    \\\"positionProfile\\\": {\\n      \\\"positionTitle\\\": \\\"Associate Developer\\\",\\n\\n      // Hirer we're posting the job ad on behalf of\\n      \\\"positionOrganizations\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n      \\\"positionFormattedDescriptions\\\": [\\n        // Short description to be shown in search results\\n        {\\n          \\\"descriptionId\\\": \\\"SearchSummary\\\",\\n          \\\"content\\\": \\\"Start your software development career with a dynamic & growing team\\\"\\n        },\\n\\n        // Detailed description that appears on the job ad\\n        {\\n          \\\"descriptionId\\\": \\\"AdvertisementDetails\\\",\\n          \\\"content\\\": \\\"A newly created role for an ambitious <b>junior</b> or <b>graduate</b> developer. Be part of a great team in the Bendigo area.\\\"\\n        }\\n      ],\\n\\n      // 70—85 Australian dollars per hour\\n      \\\"offeredRemunerationPackage\\\": {\\n        \\\"basisCode\\\": \\\"Hourly\\\",\\n        \\\"ranges\\\": {\\n          \\\"minimumAmount\\\": {\\n            \\\"value\\\": 70,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"maximumAmount\\\": {\\n            \\\"value\\\": 85,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"intervalCode\\\": \\\"Hour\\\"\\n        },\\n        // No remuneration description will be displayed to the candidate\\n        \\\"descriptions\\\": []\\n      },\\n\\n      \\\"seekAnzWorkTypeCode\\\": \\\"PartTime\\\",\\n\\n      // Information & Communication Technology > Developers/Programmers\\n      \\\"jobCategories\\\": \\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n\\n      // Australia > Victoria > Bendigo, Goldfields & Macedon Ranges > Argyle\\n      \\\"positionLocation\\\": \\\"seekAnzPublicTest:location:seek:QVS62e6K\\\",\\n\\n      // Classic Ad with the default end date in 30 calendar days\\n      \\\"postingInstructions\\\": {\\n        \\\"seekAdvertisementProductId\\\": \\\"globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7MFbCR3cKV9WdNSMC6e1vqiUJ\\\",\\n\\n        // See Idempotency section above\\n        \\\"idempotencyId\\\": \\\"6eaeb58c-cc38-4b0e-8353-daa3011f1ece\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":77,\"column\":1,\"offset\":3315},\"end\":{\"line\":159,\"column\":4,\"offset\":5997},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Success result\\\"\",\"value\":\"{\\n  \\\"postPosition\\\": {\\n    // Details of the newly created position opening\\n    \\\"positionOpening\\\": {\\n      \\\"documentId\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\"\\n      }\\n    },\\n    // Details of the newly created position profile\\n    \\\"positionProfile\\\": {\\n      \\\"profileId\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":161,\"column\":1,\"offset\":5999},\"end\":{\"line\":178,\"column\":4,\"offset\":6444},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Conflict result\\\"\",\"value\":\"{\\n  \\\"postPosition\\\": {\\n    // Details of the conflicting position opening\\n    \\\"conflictingPositionOpening\\\": {\\n      \\\"documentId\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\"\\n      }\\n    },\\n    // Details of the conflicting position profile\\n    \\\"conflictingPositionProfile\\\": {\\n      \\\"profileId\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":180,\"column\":1,\"offset\":6446},\"end\":{\"line\":197,\"column\":4,\"offset\":6910},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label": "\\\"Mutation\\\"\",\"value\":\"mutation",
        "($input:": true,
        "PostPositionInput!)": true,
        "{\\n": true,
        "": true,
        "postPosition(input:": true,
        "$input)": true,
        "...": true,
        "on": true,
        "PostPositionPayload_Success": true,
        "positionOpening": true,
        "documentId": true,
        "value\\n": true,
        "}\\n": true,
        "positionProfile": true,
        "profileId": true,
        "PostPositionPayload_Conflict": true,
        "conflictingPositionOpening": true,
        "conflictingPositionProfile": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":46,\"column\":1,\"offset\":2773},\"end\":{\"line\":75,\"column\":4,\"offset\":3313},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Variables\\\"\",\"value\":\"{\\n",
        "\\\"input\\\":": true,
        "//": true,
        "Details": true,
        "of": true,
        "the": true,
        "position": true,
        "opening": true,
        "to": true,
        "create": true,
        "along": true,
        "with": true,
        "job": true,
        "ad\\n": true,
        "\\\"positionOpening\\\":": true,
        "\\\"postingRequester\\\":": true,
        "\\\"roleCode\\\":": true,
        "\\\"Company\\\",\\n\\n": true,
        "Hirer": true,
        "that": true,
        "will": true,
        "own": true,
        "opening\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "Mary": true,
        "Manager": true,
        "is": true,
        "hiring": true,
        "this": true,
        "role": true,
        "as": true,
        "a": true,
        "manager\\n": true,
        "\\\"personContacts\\\":": true,
        "[\\n": true,
        "\\\"name\\\":": true,
        "{": true,
        "\\\"formattedName\\\":": true,
        "\\\"Mary": true,
        "Manager\\\"": true,
        "},\\n": true,
        "\\\"HiringManager\\\",\\n": true,
        "\\\"communication\\\":": true,
        "\\\"email\\\":": true,
        "[{": true,
        "\\\"address\\\":": true,
        "\\\"mary.manager@example.com\\\"": true,
        "}],\\n": true,
        "\\\"phone\\\":": true,
        "\\\"formattedNumber\\\":": true,
        "\\\"03": true,
        "4100\\\"": true,
        "}]\\n": true,
        "]\\n": true,
        "},\\n\\n": true,
        "ad": true,
        "itself\\n": true,
        "\\\"positionProfile\\\":": true,
        "\\\"positionTitle\\\":": true,
        "\\\"Associate": true,
        "Developer\\\",\\n\\n": true,
        "we're": true,
        "posting": true,
        "behalf": true,
        "of\\n": true,
        "\\\"positionOrganizations\\\":": true,
        "\\\"positionFormattedDescriptions\\\":": true,
        "Short": true,
        "description": true,
        "be": true,
        "shown": true,
        "in": true,
        "search": true,
        "results\\n": true,
        "\\\"descriptionId\\\":": true,
        "\\\"SearchSummary\\\",\\n": true,
        "\\\"content\\\":": true,
        "\\\"Start": true,
        "your": true,
        "software": true,
        "development": true,
        "career": true,
        "dynamic": true,
        "&": true,
        "growing": true,
        "team\\\"\\n": true,
        "Detailed": true,
        "appears": true,
        "\\\"AdvertisementDetails\\\",\\n": true,
        "\\\"A": true,
        "newly": true,
        "created": true,
        "for": true,
        "an": true,
        "ambitious": true,
        "<b>junior</b>": true,
        "or": true,
        "<b>graduate</b>": true,
        "developer.": true,
        "Be": true,
        "part": true,
        "great": true,
        "team": true,
        "Bendigo": true,
        "area.\\\"\\n": true,
        "],\\n\\n": true,
        "70—85": true,
        "Australian": true,
        "dollars": true,
        "per": true,
        "hour\\n": true,
        "\\\"offeredRemunerationPackage\\\":": true,
        "\\\"basisCode\\\":": true,
        "\\\"Hourly\\\",\\n": true,
        "\\\"ranges\\\":": true,
        "\\\"minimumAmount\\\":": true,
        "\\\"value\\\":": true,
        "70,\\n": true,
        "\\\"currency\\\":": true,
        "\\\"AUD\\\"\\n": true,
        "\\\"maximumAmount\\\":": true,
        "85,\\n": true,
        "\\\"intervalCode\\\":": true,
        "\\\"Hour\\\"\\n": true,
        "No": true,
        "remuneration": true,
        "displayed": true,
        "candidate\\n": true,
        "\\\"descriptions\\\":": true,
        "[]\\n": true,
        "\\\"seekAnzWorkTypeCode\\\":": true,
        "\\\"PartTime\\\",\\n\\n": true,
        "Information": true,
        "Communication": true,
        "Technology": true,
        ">": true,
        "Developers/Programmers\\n": true,
        "\\\"jobCategories\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n\\n": true,
        "Australia": true,
        "Victoria": true,
        "Bendigo,": true,
        "Goldfields": true,
        "Macedon": true,
        "Ranges": true,
        "Argyle\\n": true,
        "\\\"positionLocation\\\":": true,
        "\\\"seekAnzPublicTest:location:seek:QVS62e6K\\\",\\n\\n": true,
        "Classic": true,
        "Ad": true,
        "default": true,
        "end": true,
        "date": true,
        "calendar": true,
        "days\\n": true,
        "\\\"postingInstructions\\\":": true,
        "\\\"seekAdvertisementProductId\\\":": true,
        "\\\"globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7MFbCR3cKV9WdNSMC6e1vqiUJ\\\",\\n\\n": true,
        "See": true,
        "Idempotency": true,
        "section": true,
        "above\\n": true,
        "\\\"idempotencyId\\\":": true,
        "\\\"6eaeb58c-cc38-4b0e-8353-daa3011f1ece\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":77,\"column\":1,\"offset\":3315},\"end\":{\"line\":159,\"column\":4,\"offset\":5997},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Success",
        "result\\\"\",\"value\":\"{\\n": true,
        "\\\"postPosition\\\":": true,
        "\\\"documentId\\\":": true,
        "\\\"globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM\\\"\\n": true,
        "profile\\n": true,
        "\\\"profileId\\\":": true,
        "\\\"seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":161,\"column\":1,\"offset\":5999},\"end\":{\"line\":178,\"column\":4,\"offset\":6444},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Conflict",
        "conflicting": true,
        "\\\"conflictingPositionOpening\\\":": true,
        "\\\"conflictingPositionProfile\\\":": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":180,\"column\":1,\"offset\":6446},\"end\":{\"line\":197,\"column\":4,\"offset\":6910},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: PostPositionInput!) {
  postPosition(input: $input) {
    ... on PostPositionPayload_Success {
      positionOpening {
        documentId {
          value
        }
      }
      positionProfile {
        profileId {
          value
        }
      }
    }
    ... on PostPositionPayload_Conflict {
      conflictingPositionOpening {
        documentId {
          value
        }
      }
      conflictingPositionProfile {
        profileId {
          value
        }
      }
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "postpositionprofileforopening"
    }}>{`postPositionProfileForOpening`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/mutation/postPositionProfileForOpening"
      }}><inlineCode parentName="a">{`postPositionProfileForOpening`}</inlineCode>{` mutation`}</a>{` posts a new `}<a parentName="p" {...{
        "href": "/schema/named-type/PositionProfile"
      }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode></a>{` within an existing `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/position-openings"
      }}>{`position opening`}</a>{`.`}</p>
    <p>{`A single position opening can contain a maximum of 300 position profiles.
Once a position profile has been posted it will remain in the position opening until 180 days after its `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads#job-ad-lifecycle"
      }}>{`close date`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "30": true,
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label=\\\"Mutation\\\"\",\"value\":\"mutation ($input: PostPositionProfileForOpeningInput!) {\\n  postPositionProfileForOpening(input: $input) {\\n    ... on PostPositionProfileForOpeningPayload_Success {\\n      positionProfile {\\n        profileId {\\n          value\\n        }\\n      }\\n    }\\n    ... on PostPositionProfileForOpeningPayload_Conflict {\\n      conflictingPositionProfile {\\n        profileId {\\n          value\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":206,\"column\":1,\"offset\":7259},\"end\":{\"line\":225,\"column\":4,\"offset\":7698},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Variables\\\"\",\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"positionProfile\\\": {\\n      \\\"positionTitle\\\": \\\"Principal Developer\\\",\\n\\n      // Existing position opening created with `createPositionOpening` or `postPosition`\\n      \\\"positionOpeningId\\\": \\\"globalPublicTest:positionOpening:positions:876y9hibtZZvv6vgtQx4zd\\\",\\n\\n      // Hirer we're posting the job ad on behalf of\\n      \\\"positionOrganizations\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n      \\\"positionFormattedDescriptions\\\": [\\n        // Short description to be shown in search results\\n        {\\n          \\\"descriptionId\\\": \\\"SearchSummary\\\",\\n          \\\"content\\\": \\\"Experienced software developer wanted to lead an existing team.\\\"\\n        },\\n\\n        // Three bullet points to display in search results\\n        {\\n          \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n          \\\"content\\\": \\\"Line management required\\\"\\n        },\\n        {\\n          \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n          \\\"content\\\": \\\"Need experience with cloud deployments\\\"\\n        },\\n        {\\n          \\\"descriptionId\\\": \\\"SearchBulletPoint\\\",\\n          \\\"content\\\": \\\"Competitive benefits packages\\\"\\n        },\\n\\n        // Detailed description that appears on the job ad\\n        {\\n          \\\"descriptionId\\\": \\\"AdvertisementDetails\\\",\\n          \\\"content\\\": \\\"<p>Acme Inc is looking for an experienced software developer with management experience to lead their cloud transformation.</p><p>You should bring your passion for both technology and people to this exciting role.</p>\\\"\\n        }\\n      ],\\n\\n      // 100,000–130,000 Australian dollars per year\\n      \\\"offeredRemunerationPackage\\\": {\\n        \\\"basisCode\\\": \\\"Salaried\\\",\\n        \\\"ranges\\\": {\\n          \\\"minimumAmount\\\": {\\n            \\\"value\\\": 100000,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"maximumAmount\\\": {\\n            \\\"value\\\": 130000,\\n            \\\"currency\\\": \\\"AUD\\\"\\n          },\\n          \\\"intervalCode\\\": \\\"Year\\\"\\n        },\\n\\n        // Remuneration description visible to the candidate\\n        \\\"descriptions\\\": [\\\"Up to $130k excluding super\\\"]\\n      },\\n\\n      \\\"seekAnzWorkTypeCode\\\": \\\"FullTime\\\",\\n\\n      // YouTube video to embed above the job ad's details\\n      \\\"seekVideo\\\": {\\n        \\\"url\\\": \\\"https://www.youtube.com/embed/Q3UvigPHCjI\\\",\\n        \\\"seekAnzPositionCode\\\": \\\"Above\\\"\\n      },\\n\\n      // Information & Communication Technology > Developers/Programmers\\n      \\\"jobCategories\\\": \\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n\\n      // Australia > Queensland > Cairns & Far North Queensland > Cairns\\n      \\\"positionLocation\\\": \\\"seekAnzPublicTest:location:seek:uxLiTMNo\\\",\\n\\n      // Premium Ad with the default end date in 30 calendar days\\n      \\\"postingInstructions\\\": {\\n        \\\"seekAdvertisementProductId\\\": \\\"globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7MFaUj6LchctVWMg2zsCHxbpQ\\\",\\n\\n        // See Idempotency section above\\n        \\\"idempotencyId\\\": \\\"6eaeb58c-cc38-4b0e-8353-daa3011f1ece\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":227,\"column\":1,\"offset\":7700},\"end\":{\"line\":310,\"column\":4,\"offset\":10584},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Success result\\\"\",\"value\":\"{\\n  \\\"postPositionProfileForOpening\\\": {\\n    // Details of the newly created position profile\\n    \\\"positionProfile\\\": {\\n      \\\"profileId\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":312,\"column\":1,\"offset\":10586},\"end\":{\"line\":323,\"column\":4,\"offset\":10848},\"indent\":[1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Conflict result\\\"\",\"value\":\"{\\n  \\\"postPositionProfileForOpening\\\": {\\n    // Details of the conflicting position profile\\n    \\\"conflictingPositionProfile\\\": {\\n      \\\"profileId\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":325,\"column\":1,\"offset\":10850},\"end\":{\"line\":336,\"column\":4,\"offset\":11122},\"indent\":[1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label": "\\\"Mutation\\\"\",\"value\":\"mutation",
        "($input:": true,
        "PostPositionProfileForOpeningInput!)": true,
        "{\\n": true,
        "": true,
        "postPositionProfileForOpening(input:": true,
        "$input)": true,
        "...": true,
        "on": true,
        "PostPositionProfileForOpeningPayload_Success": true,
        "positionProfile": true,
        "profileId": true,
        "value\\n": true,
        "}\\n": true,
        "PostPositionProfileForOpeningPayload_Conflict": true,
        "conflictingPositionProfile": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":206,\"column\":1,\"offset\":7259},\"end\":{\"line\":225,\"column\":4,\"offset\":7698},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Variables\\\"\",\"value\":\"{\\n",
        "\\\"input\\\":": true,
        "\\\"positionProfile\\\":": true,
        "\\\"positionTitle\\\":": true,
        "\\\"Principal": true,
        "Developer\\\",\\n\\n": true,
        "//": true,
        "Existing": true,
        "position": true,
        "opening": true,
        "created": true,
        "with": true,
        "`createPositionOpening`": true,
        "or": true,
        "`postPosition`\\n": true,
        "\\\"positionOpeningId\\\":": true,
        "\\\"globalPublicTest:positionOpening:positions:876y9hibtZZvv6vgtQx4zd\\\",\\n\\n": true,
        "Hirer": true,
        "we're": true,
        "posting": true,
        "the": true,
        "job": true,
        "ad": true,
        "behalf": true,
        "of\\n": true,
        "\\\"positionOrganizations\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "\\\"positionFormattedDescriptions\\\":": true,
        "[\\n": true,
        "Short": true,
        "description": true,
        "to": true,
        "be": true,
        "shown": true,
        "in": true,
        "search": true,
        "results\\n": true,
        "\\\"descriptionId\\\":": true,
        "\\\"SearchSummary\\\",\\n": true,
        "\\\"content\\\":": true,
        "\\\"Experienced": true,
        "software": true,
        "developer": true,
        "wanted": true,
        "lead": true,
        "an": true,
        "existing": true,
        "team.\\\"\\n": true,
        "},\\n\\n": true,
        "Three": true,
        "bullet": true,
        "points": true,
        "display": true,
        "\\\"SearchBulletPoint\\\",\\n": true,
        "\\\"Line": true,
        "management": true,
        "required\\\"\\n": true,
        "},\\n": true,
        "\\\"Need": true,
        "experience": true,
        "cloud": true,
        "deployments\\\"\\n": true,
        "\\\"Competitive": true,
        "benefits": true,
        "packages\\\"\\n": true,
        "Detailed": true,
        "that": true,
        "appears": true,
        "ad\\n": true,
        "\\\"AdvertisementDetails\\\",\\n": true,
        "\\\"<p>Acme": true,
        "Inc": true,
        "is": true,
        "looking": true,
        "for": true,
        "experienced": true,
        "their": true,
        "transformation.</p><p>You": true,
        "should": true,
        "bring": true,
        "your": true,
        "passion": true,
        "both": true,
        "technology": true,
        "and": true,
        "people": true,
        "this": true,
        "exciting": true,
        "role.</p>\\\"\\n": true,
        "],\\n\\n": true,
        "100,000–130,000": true,
        "Australian": true,
        "dollars": true,
        "per": true,
        "year\\n": true,
        "\\\"offeredRemunerationPackage\\\":": true,
        "\\\"basisCode\\\":": true,
        "\\\"Salaried\\\",\\n": true,
        "\\\"ranges\\\":": true,
        "\\\"minimumAmount\\\":": true,
        "\\\"value\\\":": true,
        "100000,\\n": true,
        "\\\"currency\\\":": true,
        "\\\"AUD\\\"\\n": true,
        "\\\"maximumAmount\\\":": true,
        "130000,\\n": true,
        "\\\"intervalCode\\\":": true,
        "\\\"Year\\\"\\n": true,
        "Remuneration": true,
        "visible": true,
        "candidate\\n": true,
        "\\\"descriptions\\\":": true,
        "[\\\"Up": true,
        "$130k": true,
        "excluding": true,
        "super\\\"]\\n": true,
        "\\\"seekAnzWorkTypeCode\\\":": true,
        "\\\"FullTime\\\",\\n\\n": true,
        "YouTube": true,
        "video": true,
        "embed": true,
        "above": true,
        "ad's": true,
        "details\\n": true,
        "\\\"seekVideo\\\":": true,
        "\\\"url\\\":": true,
        "\\\"https://www.youtube.com/embed/Q3UvigPHCjI\\\",\\n": true,
        "\\\"seekAnzPositionCode\\\":": true,
        "\\\"Above\\\"\\n": true,
        "Information": true,
        "&": true,
        "Communication": true,
        "Technology": true,
        ">": true,
        "Developers/Programmers\\n": true,
        "\\\"jobCategories\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n\\n": true,
        "Australia": true,
        "Queensland": true,
        "Cairns": true,
        "Far": true,
        "North": true,
        "Cairns\\n": true,
        "\\\"positionLocation\\\":": true,
        "\\\"seekAnzPublicTest:location:seek:uxLiTMNo\\\",\\n\\n": true,
        "Premium": true,
        "Ad": true,
        "default": true,
        "end": true,
        "date": true,
        "calendar": true,
        "days\\n": true,
        "\\\"postingInstructions\\\":": true,
        "\\\"seekAdvertisementProductId\\\":": true,
        "\\\"globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7MFaUj6LchctVWMg2zsCHxbpQ\\\",\\n\\n": true,
        "See": true,
        "Idempotency": true,
        "section": true,
        "above\\n": true,
        "\\\"idempotencyId\\\":": true,
        "\\\"6eaeb58c-cc38-4b0e-8353-daa3011f1ece\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":227,\"column\":1,\"offset\":7700},\"end\":{\"line\":310,\"column\":4,\"offset\":10584},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Success",
        "result\\\"\",\"value\":\"{\\n": true,
        "\\\"postPositionProfileForOpening\\\":": true,
        "Details": true,
        "of": true,
        "newly": true,
        "profile\\n": true,
        "\\\"profileId\\\":": true,
        "\\\"seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":312,\"column\":1,\"offset\":10586},\"end\":{\"line\":323,\"column\":4,\"offset\":10848},\"indent\":[1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Conflict",
        "conflicting": true,
        "\\\"conflictingPositionProfile\\\":": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":325,\"column\":1,\"offset\":10850},\"end\":{\"line\":336,\"column\":4,\"offset\":11122},\"indent\":[1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: PostPositionProfileForOpeningInput!) {
  postPositionProfileForOpening(input: $input) {
    ... on PostPositionProfileForOpeningPayload_Success {
      positionProfile {
        profileId {
          value
        }
      }
    }
    ... on PostPositionProfileForOpeningPayload_Conflict {
      conflictingPositionProfile {
        profileId {
          value
        }
      }
    }
  }
}
`}</code></pre>
    <p>{`You will receive a `}<a parentName="p" {...{
        "href": "/graphql/error-responses#forbidden"
      }}><inlineCode parentName="a">{`FORBIDDEN`}</inlineCode>{` error`}</a>{` if the hirer in `}<a parentName="p" {...{
        "href": "/schema/named-type/PostPositionProfileForOpeningPositionProfileInput#/field/positionOrganizations"
      }}><inlineCode parentName="a">{`positionOrganizations`}</inlineCode></a>{` does not match the `}<a parentName="p" {...{
        "href": "/schema/named-type/PostingRequester#/field/id"
      }}><inlineCode parentName="a">{`PostingRequester.id`}</inlineCode></a>{` of the existing `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/position-openings"
      }}>{`position opening`}</a>{`.`}</p>
    <h2 {...{
      "id": "events"
    }}>{`Events`}</h2>
    <h3 {...{
      "id": "positionprofileposted"
    }}>{`PositionProfilePosted`}</h3>
    <p><a parentName="p" {...{
        "href": "/schema/named-type/PositionProfilePostedEvent"
      }}><inlineCode parentName="a">{`PositionProfilePosted`}</inlineCode>{` events`}</a>{` are emitted when job ads have been posted on the SEEK job board.
You will receive events for SEEK hirers you have a `}<a parentName="p" {...{
        "href": "/auth/hirer-relationships"
      }}><inlineCode parentName="a">{`JobPosting`}</inlineCode>{` relationship`}</a>{` with at the time of posting.`}</p>
    <p>{`In response to these events you can `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/querying-a-job-ad#positionprofile"
      }}>{`request fields from the position profile`}</a>{`.
For example, you could query:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "/schema/named-type/PositionProfile#/field/positionUri"
          }}><inlineCode parentName="a">{`positionUri`}</inlineCode>{` field`}</a>{` to provide a hirer with a link to the live job ad.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "/schema/named-type/PostingInstruction#/field/end"
          }}><inlineCode parentName="a">{`postingInstructions.end`}</inlineCode>{` field`}</a>{` to retrieve the calculated end date if one wasn’t specified during posting.`}</p>
      </li>
    </ul>
    <p>{`This is an example of a `}<a parentName="p" {...{
        "href": "/events/webhooks"
      }}>{`webhook`}</a>{` body containing a `}<inlineCode parentName="p">{`PositionProfilePosted`}</inlineCode>{` event:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsonc",
        "metastring": "sample-webhook-body",
        "sample-webhook-body": true
      }}>{`{
  "events": [
    {
      "id": "seekAnzPublicTest:event:events:UeFHntCu5giQh9Z6CRc2J7",
      "type": "PositionProfilePosted",
      "createDateTime": "2019-08-20T21:02:24.611Z",
      // This can be passed to the \`positionProfile\` query
      "positionProfileId": "seekAnzPublicTest:positionProfile:jobAd:26d9DVAzs",
      // This is only available for signed webhook subscriptions
      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  ],
  "subscriptionId": "seekAnzPublicTest:webhookSubscription:events:Kyak8JfPKhHCAXM2s9b9wX",
  "url": "https://example.com/1/2/3"
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;